import {
  GUIDELINES_API_URL,
  LATEST_GUIDELINES_API_URL,
  LATEST_GUIDELINES_FILTER_URL,
  LATEST_GUIDELINES_SEARCH_BY_DEALER_OR_GROUP_URL,
} from 'constants/ApiConstants';
import type { ApiError, MetaAction } from 'modules/global/generic.types';

import * as actionTypes from './actionTypes';
import type { Guideline } from './types';
import type * as types from './types/actions';
import { guidelinesDataCleaner } from './utils';

// The list each dashboard slice reacts to is discriminated by `meta.source`
// (see modules/guidelines/constants.ts), not by a dedicated action type.
export const fetchGuidelines = (params: types.GuidelineQueryParams = {}, meta: MetaAction = {}): types.FetchGuidelinesAction => ({
  type: actionTypes.FETCH_GUIDELINES,
  payload: {
    request: {
      method: 'GET',
      url: LATEST_GUIDELINES_FILTER_URL,
      params: {
        page: 0,
        size: 10,
        projection: 'page',
        fields: ['!dealer', '!group'].join(),
        ...params,
      },
    },
  },
  meta,
});

export const fetchGuidelinesSuccess = (response: types.FetchGuidelinesResponse, meta: MetaAction = {}): types.FetchGuidelinesSuccessAction => ({
  type: actionTypes.FETCH_GUIDELINES_SUCCESS,
  payload: response,
  meta,
});

export const fetchGuidelinesFailure = (error: ApiError, meta: MetaAction = {}): types.FetchGuidelinesFailureAction => ({
  type: actionTypes.FETCH_GUIDELINES_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const findByDealerOrGroup = (params: types.GuidelineQueryParams = {}, meta: MetaAction = {}): types.FindByDealerOrGroupAction => ({
  type: actionTypes.FIND_GUIDELINES_DEALER_OR_GROUP,
  payload: {
    request: {
      method: 'GET',
      url: LATEST_GUIDELINES_SEARCH_BY_DEALER_OR_GROUP_URL,
      params: {
        page: 0,
        size: 10,
        projection: 'page',
        ...params,
      },
    },
    ...params,
  },
  meta: {
    ...meta,
    '@app-request': true,
  },
});

export const putGuideline = (guideline: Guideline, meta: MetaAction = {}): types.PutGuidelineAction => ({
  type: actionTypes.PUT_GUIDELINE,
  payload: {
    request: {
      method: 'PUT',
      url: `${GUIDELINES_API_URL}/${guideline.id}`,
      data: guidelinesDataCleaner(guideline),
    },
  },
  meta,
});

export const postGuideline = (guideline: Guideline, meta: MetaAction = {}): types.PostGuidelineAction => ({
  type: actionTypes.POST_GUIDELINE,
  payload: {
    request: {
      method: 'POST',
      url: GUIDELINES_API_URL,
      data: guidelinesDataCleaner(guideline),
    },
  },
  meta,
});

export const deleteGuideline = ({ id, guidelineId }: { id: string; guidelineId: string }, meta: MetaAction = {}): types.DeleteGuidelineAction => ({
  type: actionTypes.DELETE_GUIDELINE,
  payload: {
    request: {
      method: 'DELETE',
      url: `${GUIDELINES_API_URL}/${id}`,
    },
    id,
    guidelineId,
  },
  meta,
});

export const deleteGuidelineSuccess = (guidelineId: string, meta: MetaAction = {}): types.DeleteGuidelineSuccessAction => ({
  type: actionTypes.DELETE_GUIDELINE_SUCCESS,
  payload: guidelineId,
  meta,
});

export const selectGuideline = (guidelineId: string, meta: MetaAction = {}): types.SelectGuidelineAction => ({
  type: actionTypes.SELECT_GUIDELINE,
  payload: guidelineId,
  meta,
});

export const fetchGuideline = (guidelineId: string, meta: MetaAction = {}): types.FetchGuidelineAction => ({
  type: actionTypes.FETCH_GUIDELINE,
  payload: {
    request: {
      method: 'GET',
      url: `${LATEST_GUIDELINES_API_URL}/${guidelineId}`,
      params: {
        projection: 'details',
      },
    },
  },
  meta,
});

export const setDashboardFilter = (field: string, value: unknown, meta: MetaAction = {}): types.SetDashboardFilterAction => ({
  type: actionTypes.SET_DASHBOARD_FILTER,
  payload: { field, value },
  meta,
});

export const setDashboardFilters = (filters: types.FilterPayload[], meta: MetaAction = {}): types.SetDashboardFiltersAction => ({
  type: actionTypes.SET_DASHBOARD_FILTERS,
  payload: filters,
  meta,
});

export const removeDashboardFilter = (field: string, value?: unknown, meta: MetaAction = {}): types.RemoveDashboardFilterAction => ({
  type: actionTypes.REMOVE_DASHBOARD_FILTER,
  payload: { field, value },
  meta,
});

export const assignBrands = (guidelineId: string, dealerId: string, brandIds: string[], meta: MetaAction = {}): types.AssignBrandsAction => ({
  type: actionTypes.ASSIGN_BRANDS_TO_GUIDELINE,
  payload: {
    request: {
      method: 'POST',
      url: `${GUIDELINES_API_URL}/${guidelineId}/assign`,
      data: { brandIds, dealerId },
    },
    brandIds,
    id: guidelineId,
  },
  meta,
});

export const cloneGuideline = ({ id, dealerId, groupId }: types.CloneGuidelinePayload, meta: MetaAction = {}): types.CloneGuidelineAction => ({
  type: actionTypes.POST_CLONE_GUIDELINE,
  payload: {
    request: {
      method: 'POST',
      url: `${GUIDELINES_API_URL}/${id}/clone`,
      data: { dealerId, groupId },
    },
  },
  meta,
});

export const openAssignmentModal = (payload: types.OpenAssignmentModalPayload, meta: MetaAction = {}): types.OpenAssignmentModalAction => ({
  type: actionTypes.OPEN_ASSIGN_MODAL,
  payload,
  meta,
});

export const closeAssignmentModal = (meta: MetaAction = {}): types.CloseAssignmentModalAction => ({
  type: actionTypes.CLOSE_ASSIGN_MODAL,
  meta,
});

export const isAssignmentModalReady = (payload?: Record<string, unknown>, meta: MetaAction = {}): types.AssignmentModalReadyAction => ({
  type: actionTypes.ASSIGN_MODAL_READY,
  payload,
  meta,
});

export const isAssignmentModalFailure = (error: unknown, meta: MetaAction = {}): types.AssignmentModalFailureAction => ({
  type: actionTypes.ASSIGN_MODAL_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const openDuplicateModal = (payload: types.OpenDuplicateModalPayload, meta: MetaAction = {}): types.OpenDuplicateModalAction => ({
  type: actionTypes.OPEN_DUPLICATE_MODAL,
  payload,
  meta,
});

export const closeDuplicateModal = (meta: MetaAction = {}): types.CloseDuplicateModalAction => ({
  type: actionTypes.CLOSE_DUPLICATE_MODAL,
  meta,
});

export const previewGuideline = ({ guidelineId, itemId }: types.PreviewGuidelinePayload, meta: MetaAction = {}): types.PreviewGuidelineAction => ({
  type: actionTypes.PREVIEW_GUIDELINE,
  payload: {
    guidelineId,
    itemId,
    request: {
      method: 'POST',
      url: `${GUIDELINES_API_URL}/${guidelineId}/items/${itemId}/preview`,
    },
  },
  meta,
});

export const previewGuidelineSuccess = (
  { guidelineId, itemId, previews }: types.PreviewGuidelineSuccessPayload,
  meta: MetaAction = {}
): types.PreviewGuidelineSuccessAction => ({
  type: actionTypes.PREVIEW_GUIDELINE_SUCCESS,
  payload: { guidelineId, itemId, previews },
  meta,
});

export const previewGuidelineFailure = (
  { guidelineId, itemId, error }: types.PreviewGuidelineFailurePayload,
  meta: MetaAction = {}
): types.PreviewGuidelineFailureAction => ({
  type: actionTypes.PREVIEW_GUIDELINE_FAILURE,
  payload: { guidelineId, itemId, error },
  error: true,
  meta,
});
